import React, { useContext } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import { mediaQueryUp } from 'components/utils/breakpoint'
import BedbankOfferSummary from './BedbankOfferSummary'
import ShareWithFriendsButton from 'components/Common/ShareWithFriendsButton'
import { connect } from 'react-redux'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import ShareModal from 'components/Common/Modals/ShareModal'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import { HeaderHeightContext } from 'contexts/HeaderStateContext'
import { useAppSelector } from 'hooks/reduxHooks'
import BedbankPropertyDetails from './BedbankPropertyDetails'
import ExportMediaContentButton from 'components/Common/ExportMediaContentButton/ExportMediaContentButton'
import config from 'constants/config'
import { buildAgentHubQuotingURL } from 'lib/agentHub/quoting'
import { OFFER_VIEWS_THRESHOLD } from 'constants/offer'
import OfferViewsUrgencyBanner from '../OfferViewsUrgencyBanner'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import { ShareConfig } from 'components/Common/Modals/ShareModal/ShareModal'
import { getCanAgentExportMedia } from 'agentHub/selectors/agentHubSelectors'

const RootContainer = styled(LayoutContainer)`
  padding-top: ${rem(20)};
  padding-bottom: ${rem(56)};
`

const SummaryArea = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    position: relative;
    align-self: stretch;
    display: block;
  }
`

const StickySummaryContent = styled.div`
  position: sticky;
  top: 0;
`

interface Props {
  offer: App.BedbankOffer;
  duration: number;
  checkInDate?: string;
  checkOutDate?: string;
  onViewPackageOptions?: () => void;
  rooms: Array<App.Occupants>;
  loadingPrices?: boolean;
  flightPrice?: number;
  flightsEnabled: boolean;
  shareModalConfig: ShareConfig
}

function BedbankOfferIntro(props: Props) {
  const {
    checkInDate,
    checkOutDate,
    offer,
    onViewPackageOptions,
    rooms,
    loadingPrices,
    flightsEnabled,
    flightPrice,
    duration,
    shareModalConfig,
  } = props

  const isTabletScreenSizeAndUp = useScreenSizeUp('tablet')

  const { heights } = useContext(HeaderHeightContext)
  const location = offer.property.locationTitleOverride ?
    offer.property.locationTitleOverride :
      [offer.property.address.city, offer.property.address.stateProvinceName]
        .filter(Boolean)
        .join(', ')

  const hasPromotions = offer.promotions.length > 0

  const bedBankOfferPromotionEndDate = offer.promotions[0]?.periodSchedule.to

  const region = useAppSelector(state => state.geo.currentRegionCode)
  const currency = useAppSelector(state => state.geo.currentCurrency)
  const isExportMediaEnabled = config.EXPORT_MEDIA_CONTENT_ENABLED
  const isAgentHubEnabled = config.agentHub.isEnabled
  const mediaExportURL = isAgentHubEnabled ? buildAgentHubQuotingURL(offer.id, { currency, region }) : undefined
  const canAgentExportMedia = getCanAgentExportMedia

  const showOfferViews = offer.stats.views > OFFER_VIEWS_THRESHOLD

  return (
    <RootContainer>
      <Group direction="vertical" gap={16}>
        <Group
          direction="vertical"
          tabletDirection="horizontal-reverse"
          horizontalAlign="space-between"
          verticalAlign="center"
          gap={4}
          noDisplayWhenEmpty
        >
          {isTabletScreenSizeAndUp && (
            <Group
              direction="horizontal"
              gap={8}
              verticalAlign="center"
              horizontalAlign="end"
            >
              {isExportMediaEnabled && canAgentExportMedia && <TripGuard>
                <ExportMediaContentButton href={mediaExportURL} target="_blank" />
              </TripGuard>}
              <TripGuard>
                <ShareModal shareConfig={shareModalConfig}>
                  <ShareWithFriendsButton />
                </ShareModal>
                <BookmarkButton offer={offer} />
              </TripGuard>

              <TripGuard inverse>
                <ShareModal shareConfig={shareModalConfig}>
                  <ShareWithFriendsButton horizontalOutdent={isExportMediaEnabled ? undefined : 'end'} />
                </ShareModal>
                <BookmarkButton offer={offer} />
              </TripGuard>
              {isExportMediaEnabled && canAgentExportMedia && <TripGuard inverse>
                <ExportMediaContentButton href={mediaExportURL} target="_blank" />
              </TripGuard>}
            </Group>
          )}

          {hasPromotions && <LabelGroup>
            <ProductTypeLabel productType="limited_time_special" />

            {bedBankOfferPromotionEndDate && (
              <OfferUrgencyCountdownLabel
                endDate={bedBankOfferPromotionEndDate}
                tagPlace="bedbank-offer-intro"
              />
            )}
          </LabelGroup>}
        </Group>
        {showOfferViews && <CSSBreakpoint max="mobile">
          <OfferViewsUrgencyBanner offer={offer} />
        </CSSBreakpoint>}
        <Group direction="horizontal" gap={48}>
          <BedbankPropertyDetails
            offer={offer}
            location={location}
            hasPromotions={hasPromotions}
            checkInDate={checkInDate}
            checkOutDate={checkOutDate}
            duration={duration}
            rooms={rooms}
            flightPrice={flightPrice}
            flightsEnabled={flightsEnabled}
          />
          <SummaryArea>
            <StickySummaryContent
              style={{
                top: rem(heights.bedbankOfferHeader + 16),
              }}
            >
              {showOfferViews && <OfferViewsUrgencyBanner offer={offer} />}
              <BedbankOfferSummary
                loadingPrices={loadingPrices}
                onViewPackageOptions={onViewPackageOptions}
                checkInDate={checkInDate}
                checkOutDate={checkOutDate}
                duration={duration}
                offer={offer}
                rooms={rooms}
                flightPrice={flightPrice}
                flightsEnabled={flightsEnabled}
              />
            </StickySummaryContent>
          </SummaryArea>
        </Group>
      </Group>
    </RootContainer>
  )
}

function mapStateToProps(state: App.State, ownProps: Partial<Props>) {
  return {
    loadingPrices: !!state.offer.offerRatesLoading[ownProps.offer!.id],
  }
}

export default connect(mapStateToProps)(BedbankOfferIntro)
